'use client'

import type { RefObject } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { HeaderSearchFallback } from './HeaderSearchFallback'
import { isActiveStoreTab, storeNavigation } from '@/lib/siteHeader/storeNavigation'
import classes from './ExpandedHeader.module.css'

interface ExpandedHeaderProps {
    /** Placeholder div that should display search while the expanded header is showing. */
    searchTargetRef: RefObject<HTMLDivElement>
    /** When false, the portal has moved to the compact bar; the slot still reserves its space. */
    showSearchSlot: boolean
    /** Text to show in the fallback field so refresh does not flash an empty input. */
    searchQuery?: string
}

/**
 * Title, search slot, and shop pills for the expanded site header.
 *
 * The search target contains a server-rendered fallback until the interactive
 * search portal attaches there. Category pills are real routes.
 */
export function ExpandedHeader({ searchTargetRef, showSearchSlot, searchQuery }: ExpandedHeaderProps) {
    const pathname = usePathname()

    return (
        <section className={classes.root}>
            <h1 className={classes.title}>
                Your everyday <span className={classes.titleHighlight}>healthcare</span> store
            </h1>

            <div className={classes.searchTarget} ref={searchTargetRef}>
                {showSearchSlot && <HeaderSearchFallback searchQuery={searchQuery} />}
            </div>

            <nav className={classes.categories} aria-label="Care categories">
                {storeNavigation.map((category) => {
                    const isActive = isActiveStoreTab(pathname, category.href)

                    return (
                        <Link
                            className={isActive ? `${classes.category} ${classes.categoryActive}` : classes.category}
                            href={category.href}
                            key={category.href}
                            aria-current={isActive ? 'page' : undefined}
                            prefetch={true}
                        >
                            {category.label}
                        </Link>
                    )
                })}
            </nav>
        </section>
    )
}
